import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { userApi } from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { User, SignOut, House, Calendar, ShieldCheck, Gift, ArrowsLeftRight, ListDashes, Key, CaretDown, X, ChatsCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

export default function Navbar() {
  const { user, token, logout, activeMode, switchMode } = useAuth();
  const navigate = useNavigate();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const userMenuRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleModeSwitch = () => {
    if (activeMode === 'guest' && user?.is_host) {
      switchMode('host');
      navigate('/host/dashboard');
    } else if (activeMode === 'host') {
      switchMode('guest');
      navigate('/');
    } else if (activeMode === 'guest' && !user?.is_host) {
      // User is not a host yet, take them to become a host
      navigate('/become-host');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    
    setPasswordLoading(true);
    try {
      await userApi.changePassword({
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, token);
      
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <nav className="sticky top-0 z-50 relative">
      {/* Dark graphical background */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.95), rgba(0,0,0,0.85)), url('https://images.unsplash.com/photo-1557683316-973673baf926?w=1920&q=80')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative">
        <div className="flex items-center justify-between h-24 sm:h-28 md:h-32">
          {/* Logo - oversized, extends beyond navbar */}
          <Link 
            to="/" 
            data-testid="navbar-logo"
            className="flex items-center hover:opacity-90 transition-opacity relative z-10 pointer-events-auto"
          >
            <img 
              src="/images/logo-full-transparent.png" 
              alt="100Nook" 
              className="h-48 sm:h-64 md:h-72 w-auto -my-12 sm:-my-16 md:-my-20 pointer-events-none"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {user ? (
              <>
                {/* Admin users only see Admin Dashboard - no mode switching */}
                {user.role === 'admin' ? (
                  <>
                    <Link
                      to="/admin/dashboard"
                      data-testid="admin-dashboard-link"
                      className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-all text-[#D4AF37]"
                    >
                      <ShieldCheck size={18} weight="duotone" />
                      <span className="text-sm">Admin Dashboard</span>
                    </Link>
                  </>
                ) : (
                  <>
                    {/* Mode Switcher - Only for non-admin users */}
                    <button
                      onClick={handleModeSwitch}
                      data-testid="mode-switcher"
                      className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/30 hover:border-[#D4AF37] transition-all group"
                    >
                      <ArrowsLeftRight size={18} weight="duotone" className="text-[#D4AF37]" />
                      <span className="text-sm text-zinc-300 group-hover:text-white">
                        {activeMode === 'host' ? 'Switch to Guest' : user?.is_host ? 'Switch to Host' : 'Become a Host'}
                      </span>
                    </button>

                    <Link
                      to="/verification"
                      className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                    >
                      <ShieldCheck size={20} weight="duotone" />
                      {!user.email_verified || !user.phone_verified || !user.id_verified ? (
                        <span className="text-yellow-500">Verify</span>
                      ) : (
                        <span>Verified</span>
                      )}
                    </Link>
                    
                    <Link
                      to="/referrals"
                      className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                    >
                      <Gift size={20} weight="duotone" />
                      Referrals
                    </Link>

                    {/* Show contextual links based on active mode */}
                    {activeMode === 'host' && user?.is_host && (
                      <Link
                        to="/host/dashboard"
                        data-testid="host-dashboard-link"
                        className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                      >
                        <House size={20} weight="duotone" />
                        Host Dashboard
                      </Link>
                    )}
                    
                    {activeMode === 'guest' && (
                      <Link
                        to="/guest/bookings"
                        data-testid="guest-bookings-link"
                        className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                      >
                        <Calendar size={20} weight="duotone" />
                        My Bookings
                      </Link>
                    )}
                    
                    {activeMode === 'guest' && (
                      <Link
                        to="/guest/messages"
                        data-testid="guest-messages-link"
                        className="text-zinc-400 hover:text-white transition-colors flex items-center gap-2"
                      >
                        <ChatsCircle size={20} weight="duotone" />
                        Messages
                      </Link>
                    )}
                  </>
                )}

                <div className="flex items-center gap-4 pl-4 border-l border-white/10 relative" ref={userMenuRef}>
                  <button 
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-2 hover:bg-white/5 rounded px-2 py-1 transition-colors"
                    data-testid="user-menu-button"
                  >
                    <div className="flex flex-col items-end">
                      <span className="text-white text-sm flex items-center gap-2">
                        <User size={16} weight="duotone" />
                        {user.name}
                      </span>
                      {user.role !== 'admin' && (
                        <span className="text-xs text-[#D4AF37]">
                          {activeMode === 'host' ? 'Host Mode' : 'Guest Mode'}
                        </span>
                      )}
                      {user.role === 'admin' && (
                        <span className="text-xs text-[#D4AF37]">
                          Admin
                        </span>
                      )}
                    </div>
                    <CaretDown size={16} className={`text-zinc-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* User Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-[#121212] border border-white/10 shadow-xl z-50">
                      <button
                        onClick={() => {
                          setShowPasswordModal(true);
                          setShowUserMenu(false);
                        }}
                        data-testid="change-password-btn"
                        className="w-full text-left px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 flex items-center gap-2 transition-colors"
                      >
                        <Key size={18} weight="duotone" />
                        Change Password
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowUserMenu(false);
                        }}
                        data-testid="logout-dropdown-btn"
                        className="w-full text-left px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 flex items-center gap-2 border-t border-white/10 transition-colors"
                      >
                        <SignOut size={18} weight="duotone" />
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/auth"
                  data-testid="signin-link"
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  Sign In
                </Link>
                <Button
                  onClick={() => navigate('/auth')}
                  data-testid="become-host-button"
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold px-6 py-2"
                >
                  Become a Host
                </Button>
              </>
            )}
          </div>

          {/* Mobile Menu */}
          <div className="md:hidden flex items-center gap-4">
            {user ? (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowMobileMenu(!showMobileMenu)}
                  className="text-zinc-400 hover:text-white p-2"
                >
                  <ListDashes size={24} weight="duotone" />
                </button>
              </div>
            ) : (
              <Button
                onClick={() => navigate('/auth')}
                className="bg-[#D4AF37] hover:bg-[#B5952F] text-black font-semibold px-4 py-2 text-sm"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {showMobileMenu && user && (
          <div className="md:hidden border-t border-white/10 py-4 space-y-3 relative z-[60]">
            {/* Admin users only see Admin Dashboard */}
            {user.role === 'admin' ? (
              <>
                <Link
                  to="/admin/dashboard"
                  data-testid="mobile-admin-dashboard-link"
                  onClick={() => setShowMobileMenu(false)}
                  className="flex items-center gap-2 px-4 py-2 text-[#D4AF37] hover:text-white"
                >
                  <ShieldCheck size={20} weight="duotone" />
                  Admin Dashboard
                </Link>
                <button
                  onClick={() => {
                    setShowPasswordModal(true);
                    setShowMobileMenu(false);
                  }}
                  data-testid="mobile-admin-change-password-btn"
                  className="w-full text-left px-4 py-2 text-zinc-400 hover:text-white flex items-center gap-2"
                >
                  <Key size={20} weight="duotone" />
                  Change Password
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => { handleModeSwitch(); setShowMobileMenu(false); }}
                  className="w-full text-left px-4 py-2 text-zinc-400 hover:text-white flex items-center gap-2"
                >
                  <ArrowsLeftRight size={20} weight="duotone" />
                  {activeMode === 'host' ? 'Switch to Guest' : user?.is_host ? 'Switch to Host' : 'Become a Host'}
                </button>
                
                {activeMode === 'host' && user?.is_host && (
                  <Link
                    to="/host/dashboard"
                    onClick={() => setShowMobileMenu(false)}
                    className="block px-4 py-2 text-zinc-400 hover:text-white flex items-center gap-2"
                  >
                    <House size={20} weight="duotone" />
                    Host Dashboard
                  </Link>
                )}
                
                {activeMode === 'guest' && (
                  <Link
                    to="/guest/bookings"
                    onClick={() => setShowMobileMenu(false)}
                    className="block px-4 py-2 text-zinc-400 hover:text-white flex items-center gap-2"
                  >
                    <Calendar size={20} weight="duotone" />
                    My Bookings
                  </Link>
                )}
                
                {activeMode === 'guest' && (
                  <Link
                    to="/guest/messages"
                    onClick={() => setShowMobileMenu(false)}
                    className="block px-4 py-2 text-zinc-400 hover:text-white flex items-center gap-2"
                  >
                    <ChatsCircle size={20} weight="duotone" />
                    Messages
                  </Link>
                )}
                
                <Link
                  to="/verification"
                  onClick={() => setShowMobileMenu(false)}
                  className="block px-4 py-2 text-zinc-400 hover:text-white flex items-center gap-2"
                >
                  <ShieldCheck size={20} weight="duotone" />
                  Verification
                </Link>
                
                <Link
                  to="/referrals"
                  onClick={() => setShowMobileMenu(false)}
                  className="block px-4 py-2 text-zinc-400 hover:text-white flex items-center gap-2"
                >
                  <Gift size={20} weight="duotone" />
                  Referrals
                </Link>
                
                <button
                  onClick={() => {
                    setShowPasswordModal(true);
                    setShowMobileMenu(false);
                  }}
                  className="w-full text-left px-4 py-2 text-zinc-400 hover:text-white flex items-center gap-2"
                >
                  <Key size={20} weight="duotone" />
                  Change Password
                </button>
              </>
            )}
            
            <button
              onClick={() => { handleLogout(); setShowMobileMenu(false); }}
              className="w-full text-left px-4 py-2 text-red-400 hover:text-red-300 flex items-center gap-2"
            >
              <SignOut size={20} weight="duotone" />
              Sign Out
            </button>
          </div>
        )}
      </div>
      
      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
          <div className="bg-[#121212] border border-white/10 max-w-md w-full">
            <div className="p-6 border-b border-white/10 flex justify-between items-center">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Key size={24} className="text-[#D4AF37]" />
                Change Password
              </h3>
              <button 
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                }}
                className="text-zinc-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleChangePassword} className="p-6 space-y-4">
              <div>
                <Label htmlFor="currentPassword" className="text-white mb-2 block">
                  Current Password
                </Label>
                <Input
                  id="currentPassword"
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                  required
                  data-testid="current-password-input"
                  className="bg-[#0a0a0a] border-white/20 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="newPassword" className="text-white mb-2 block">
                  New Password
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  placeholder="Enter new password (min 8 characters)"
                  required
                  minLength={8}
                  data-testid="new-password-input"
                  className="bg-[#0a0a0a] border-white/20 text-white"
                />
              </div>
              
              <div>
                <Label htmlFor="confirmPassword" className="text-white mb-2 block">
                  Confirm New Password
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                  required
                  data-testid="confirm-password-input"
                  className="bg-[#0a0a0a] border-white/20 text-white"
                />
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="border-white/20 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={passwordLoading}
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  data-testid="submit-password-change"
                >
                  {passwordLoading ? 'Changing...' : 'Change Password'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </nav>
  );
}